"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

export default function CommissaryBanner() {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1000);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden group cursor-pointer" style={{ minHeight: "280px" }}>

      {/* LAYERED BACKGROUND */}
      <div className="absolute inset-0 bg-[#0a0a0a]" />

      {/* Diagonal stripes — caution tape feel */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(220,38,38,0.3) 20px, rgba(220,38,38,0.3) 22px)",
      }} />

      {/* Noise grain */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }} />

      {/* Red glow orbs */}
      <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-red-600/15 rounded-full blur-[80px]" />
      <div className="absolute -bottom-20 -left-10 w-[250px] h-[250px] bg-red-900/20 rounded-full blur-[70px]" />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] bg-red-500/10 rounded-full blur-[60px] transition-all duration-1000 ${pulse ? "scale-150 opacity-100" : "scale-100 opacity-50"}`} />

      {/* Animated scanlines */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)",
      }} />

      {/* Border */}
      <div className="absolute inset-0 rounded-2xl border border-red-800/20 group-hover:border-red-600/30 transition-colors duration-500" />

      {/* TOP CAUTION STRIPE */}
      <div className="absolute top-0 left-0 right-0 h-1">
        <div className="h-full" style={{
          background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 10px, transparent 10px, transparent 20px, #dc2626 20px)",
          opacity: 0.6,
        }} />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1">
        <div className="h-full" style={{
          background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 10px, transparent 10px, transparent 20px, #dc2626 20px)",
          opacity: 0.6,
        }} />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 p-8 md:p-12 flex flex-col items-center text-center">

        {/* Stencil label */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px w-8 bg-gradient-to-r from-transparent to-red-500" />
          <span className="text-[9px] font-black text-red-400 uppercase tracking-[0.4em] font-mono">Property of CellBloxk Barbie</span>
          <div className="h-px w-8 bg-gradient-to-l from-transparent to-red-500" />
        </div>

        {/* Main headline — stencil style */}
        <h2 className="text-4xl md:text-6xl font-black uppercase text-white leading-[0.85] tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.08em" }}>
          COMMISSARY
        </h2>
        <h3 className="text-2xl md:text-3xl font-black uppercase leading-[0.85] tracking-tight mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-pink-500">WINDOW OPEN</span>
        </h3>

        {/* Credits display — like a price board */}
        <div className="flex items-center gap-4 md:gap-6 mb-8">
          {[
            { amount: "500", price: "$4.99", label: "Starter" },
            { amount: "3,000", price: "$24.99", label: "Bag", hot: true },
            { amount: "15,000", price: "$99.99", label: "Whale" },
          ].map((pkg) => (
            <div key={pkg.label} className={`relative flex flex-col items-center px-4 py-3 rounded-xl border transition-all ${
              pkg.hot
                ? "border-red-600/40 bg-red-950/30 scale-105"
                : "border-neutral-700 bg-neutral-900/60"
            }`}>
              {pkg.hot && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[7px] font-black bg-red-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Popular</span>
              )}
              <span className="text-lg md:text-xl font-black text-white font-mono">{pkg.amount}</span>
              <span className="text-[9px] text-neutral-400 uppercase tracking-wider">{pkg.label}</span>
              <span className="text-[11px] font-bold text-green-400 mt-1 font-bold">{pkg.price}</span>
            </div>
          ))}
        </div>

        {/* CTA button */}
        <Link href="/subscribe" className="relative group/btn">
          <div className={`absolute inset-0 rounded-xl bg-red-600 blur-md transition-all duration-1000 ${pulse ? "opacity-60 scale-105" : "opacity-30 scale-100"}`} />
          <div className="relative px-10 md:px-14 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black uppercase text-sm md:text-base tracking-[0.15em] rounded-xl transition-all shadow-[0_4px_30px_rgba(220,38,38,0.4)] hover:shadow-[0_4px_40px_rgba(220,38,38,0.6)] group-hover/btn:scale-[1.02]">
            HIT THE WINDOW
          </div>
        </Link>

        {/* Bottom text */}
        <p className="text-[10px] text-neutral-500 mt-6 uppercase tracking-widest font-mono">
          All commissary is non-refundable. 80% goes to inmates.
        </p>
      </div>
    </div>
  );
}
